import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
    Component,
    h,
    Host,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { ApSkipContentEvents } from '../../models/appInsights/events/ap-skip-content.events';
import store from '../../store/store';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getTheme } from '../../utils/themeHelper';
import { FOCUSABLE_ELEMENTS_QUERY } from './constants';

@Component({
    tag: 'ap-skip-content',
    styleUrl: 'ap-skip-content.scss',
    shadow: true,
})
export class ApSkipContent {
    /**
     * The element to be focused when the skip content button is clicked.
     * It can be directly a focusable element or a container element which contains focusable elements.
     *
     * @memberof ApSkipContent
     * @default FOCUSABLE_ELEMENTS_QUERY
     */
    @Prop() skipToTarget: string = FOCUSABLE_ELEMENTS_QUERY;

    /**
     * Timeout in ms for trying to find a focusable element within `skipToTarget`.
     *
     * @memberof ApSkipContent
     * @default 1000
     */
    @Prop() waitForSkipTarget: number = 1000;

    /**
     * Property (not attribute) to attach the parent element
     * that will have observers attached to it
     *
     * @memberof ApSkipContent
     * @default document.body
     */
    @Prop() rootRef: Element = document.body;

    /**
    * Theme override to bypass store state
    * @memberof ApSkipContent
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Keep a debounce on the click event to avoid multiple clicks
     */
    private _lastTriggered?: Date;

    /**
     * Ref for timeout so it can be cleared early if blur happens
     */
    private _timeoutRef?: NodeJS.Timeout;

    /**
     * Check if the element has been clicked
     */
    private _clicked = false;

    /**
     * Mutation observer reference stored for disconnecting on changes
     */
    private _activeObserver: MutationObserver | null = null;

    disconnectedCallback() {
        this._activeObserver?.disconnect();
    }

    render() {
        return (
            <Host class={getTheme(store, this.theme)}>
                <button
                    class="portal-button primary skip-content"
                    data-cy="skip-content"
                    onFocus={() => {
                        this._clicked = false;
                        this._clearTimers();
                        Logger.logCustomEvent(ApSkipContentEvents.SkipContentDisplayed);
                    }}
                    onBlur={() => {
                        this._clearTimers();
                        if (!this._clicked) {
                            Logger.logCustomEvent(ApSkipContentEvents.SkipContentBluredWithoutClick);
                        }
                    }}
                    onClick={ () => this._onActionHandler() }
                    onKeyUp={ (e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            this._onActionHandler();
                        }
                    }}
                >
                    {t('skip-content')}
                </button>
            </Host>
        );
    }

    private _onActionHandler = () => {
        this._clicked = true;
        this._focusTarget();
    };

    private _clearTimers = () => {
        this._lastTriggered = undefined;
        clearTimeout(this._timeoutRef);
        this._timeoutRef = undefined;
        this._activeObserver?.disconnect();
    };

    private async _getTargetEl() {
        try {
            this._activeObserver?.disconnect();
            return await this._waitForTargetEl(this.rootRef, this.skipToTarget, this.waitForSkipTarget);
        } catch (err) {
            Logger.logCustomEvent(ApSkipContentEvents.SkipContentQueryError, {
                targetElement: this.skipToTarget,
                errorMessage: err,
            }, SeverityLevel.Error);

            return null;
        }
    }

    private _getFocusableElement(elem: HTMLElement | null, skipToTarget: string) {
        let element = elem;

        if (skipToTarget !== FOCUSABLE_ELEMENTS_QUERY) {
            // include the target element itself in the search
            element = (element?.parentElement ?? element)?.querySelector(FOCUSABLE_ELEMENTS_QUERY) ?? null;
        }

        return element;
    }

    private _waitForTargetEl(rootRef: Element, skipToTarget: string, waitForSkipTarget: number) {
        return new Promise<HTMLElement | null>((resolve, reject) => {
            const element = this._getFocusableElement(rootRef.querySelector<HTMLElement>(skipToTarget), skipToTarget);

            if (element) {
                return resolve(element);
            }

            this._activeObserver = new MutationObserver(mutations => {
                mutations.forEach((mutation) => {
                    if (!mutation.addedNodes) {
                        return;
                    }

                    for (const node of mutation.addedNodes) {
                        const htmlNode = node as HTMLElement;

                        if (htmlNode.matches(skipToTarget)) {
                            const target = this._getFocusableElement(htmlNode, skipToTarget);

                            if (target) {
                                this._activeObserver!.disconnect();
                                return resolve(target);
                            }
                        }
                    }
                });
            });

            this._timeoutRef = setTimeout(() => {
                this._activeObserver?.disconnect();
                reject(`Target element not found in ${waitForSkipTarget} ms`);
            }, waitForSkipTarget);

            this._activeObserver.observe(rootRef, {
                childList: true,
                subtree: true,
            });
        });
    }

    private async _focusTarget() {
        if (this._shouldIgnoreEvent()) {
            return;
        }
        this._lastTriggered = new Date();

        Logger.logCustomEvent(ApSkipContentEvents.SkipContentClicked, { targetElement: this.skipToTarget });

        const target = await this._getTargetEl();

        if (!target) {
            // blur the button if it failed to find the target
            (document.activeElement as HTMLElement)?.blur();
            return;
        }

        target.focus();
        Logger.logCustomEvent(ApSkipContentEvents.SkipContentSuccess, {
            targetElement: this.skipToTarget,
            focusedElement: target.outerHTML,
        });
    }

    private _shouldIgnoreEvent() {
        return this._lastTriggered && new Date().getTime() - this._lastTriggered.getTime() < this.waitForSkipTarget;
    }
}
